'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import ReactDOM from 'react-dom'
import { useParams } from 'react-router'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { differenceInDays } from 'date-fns'

// walrus
import Box from '@components/kit/box'
import { HeaderBackButton } from '@components/kit/header'
import Text from '@components/kit/text'
import Button from '@components/kit/button'
import AppShell from '@components/ui/app-shell'
import PageHeader from '@components/ui/page-header'
import { Column, Row } from '@components/kit/grid'
import { BoxLink } from '@components/kit/link'
import FlatList from '@components/kit/flat-list'
import Loading from '@components/kit/loading'
import { OpportunitiesIcon, ToggleArrowIcon } from '@components/kit/icons'
import Pressable from '@components/kit/pressable'
import { SelectButton, SelectMenu, useSelect } from '@components/kit/select'
import CheckBoxWrapper from '@components/kit/form/inputs/checkbox-input/checkbox-wrapper'
import { CompanyMemberItem, useMembersStore } from '@store/Members'
import {
  activityLabelMap,
  formatActivity
} from '../../home/members-needs-attention.web'
import { getScrollbarWidth } from '@utils/dom'

const PAGE_SIZE = 50

/* -----------------------------------------------------------------------------
 * ActivityFilterSelect
 * -------------------------------------------------------------------------- */

const ACTIVITY_OPTIONS = Object.entries(activityLabelMap).map(
  ([value, label]) => ({ value, label })
)

type ActivityFilterSelectProps = {
  selectedActivities: string[]
  onFilter: (activities: string[]) => void
}

function ActivityFilterSelect({
  selectedActivities,
  onFilter
}: ActivityFilterSelectProps) {
  const buttonRef = useRef(null)
  const menuRef = useRef(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [localSelected, setLocalSelected] =
    useState<string[]>(selectedActivities)
  const [committed, setCommitted] = useState<string[]>(selectedActivities)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(true)

  const { getToggleButtonProps, getMenuProps, closeMenu, isOpen } = useSelect({
    buttonRef,
    menuRef,
    items: ACTIVITY_OPTIONS
  })

  const checkScroll = () => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollUp(el.scrollTop > 0)
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1)
  }

  useEffect(() => {
    if (!isOpen) return
    requestAnimationFrame(checkScroll)
    const el = scrollRef.current
    if (!el) return
    el.addEventListener('scroll', checkScroll, { passive: true })
    return () => el.removeEventListener('scroll', checkScroll)
  }, [isOpen])

  const handleToggle = (value: string) => {
    setLocalSelected(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    )
  }

  const handleFilter = () => {
    onFilter(localSelected)
    setCommitted(localSelected)
    closeMenu()
  }

  const handleClear = () => {
    setLocalSelected([])
    setCommitted([])
    onFilter([])
    closeMenu()
  }

  const isActive = selectedActivities.length > 0

  const sortedOptions = useMemo(() => {
    if (committed.length === 0) return ACTIVITY_OPTIONS
    return [...ACTIVITY_OPTIONS].sort((a, b) => {
      const aIn = committed.includes(a.value)
      const bIn = committed.includes(b.value)
      if (aIn && !bIn) return -1
      if (!aIn && bIn) return 1
      return 0
    })
  }, [committed])

  const fadeStyle = {
    position: 'absolute' as const,
    left: 0,
    right: 0,
    height: 52,
    pointerEvents: 'none' as const,
    zIndex: 1
  }

  return (
    <>
      <Box ref={buttonRef} width='100%' height='48'>
        <SelectButton
          {...getToggleButtonProps()}
          width='10'
          height='10'
          px='16'
          py='00'
          size='medium'
          bg='white'
          showIcon={false}
        >
          <Text fontSize={12} textColor='purple' fontWeight='bold'>
            LAST ACTIVITY
          </Text>
          <OpportunitiesIcon
            stroke={isActive ? 'purple' : 'shade04'}
            fill={isActive ? 'purple' : 'shade04'}
            width={10}
            height={10}
            ml='08'
          />
        </SelectButton>
      </Box>
      {ReactDOM.createPortal(
        <SelectMenu {...getMenuProps({ ref: menuRef })} minWidth='220px'>
          <div style={{ position: 'relative' }}>
            <Box ref={scrollRef} maxHeight='260px' overflowY='scroll'>
              {sortedOptions.map(option => (
                <Pressable
                  key={option.value}
                  onPress={() => handleToggle(option.value)}
                  flexDirection='row'
                  alignItems='center'
                  mt='12'
                  p='00'
                >
                  <CheckBoxWrapper
                    isChecked={localSelected.includes(option.value)}
                    size={20}
                  >
                    {null}
                  </CheckBoxWrapper>
                  <Text ml='08' fontSize={13} textColor='purple'>
                    {option.label}
                  </Text>
                </Pressable>
              ))}
            </Box>
            {canScrollUp && (
              <div
                style={{
                  ...fadeStyle,
                  top: 0,
                  background:
                    'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)'
                }}
              />
            )}
            {canScrollDown && (
              <div
                style={{
                  ...fadeStyle,
                  bottom: 0,
                  background:
                    'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)'
                }}
              />
            )}
          </div>
          <Row mt='12' gap='08'>
            <Button
              shape='rectangular'
              size='small'
              variant='violet'
              flex='1 1 auto'
              onPress={handleFilter}
            >
              Filter
            </Button>
            <Button
              shape='rectangular'
              size='small'
              variant='gray'
              flex='1 1 auto'
              onPress={handleClear}
            >
              Clear
            </Button>
          </Row>
        </SelectMenu>,
        document.getElementById('root') as Element
      )}
    </>
  )
}

/* -----------------------------------------------------------------------------
 * MemberHeader
 * -------------------------------------------------------------------------- */

type SortOrder = 'ASC' | 'DESC'

const SORT_ORDER_API: Record<
  SortOrder,
  'LAST_ENGAGEMENT_ASC' | 'LAST_ENGAGEMENT_DESC'
> = {
  ASC: 'LAST_ENGAGEMENT_ASC',
  DESC: 'LAST_ENGAGEMENT_DESC'
}

type MemberHeaderProps = {
  sortOrder: SortOrder
  onSortToggle: () => void
  selectedActivities: string[]
  onActivityFilter: (activities: string[]) => void
  scrollbarWidth: number
}

function MemberHeader({
  sortOrder,
  onSortToggle,
  selectedActivities,
  onActivityFilter,
  scrollbarWidth
}: MemberHeaderProps) {
  return (
    <Row
      borderTopLeftRadius='medium'
      borderTopRightRadius='medium'
      backgroundColor='white'
      borderColor='shade02'
      borderStyle='solid'
      borderRightWidth={1}
      borderBottomWidth={1}
      maxHeight={40}
    >
      <Column width={110} paddingX='16' paddingY='04' justifyContent='center'>
        <Text fontSize={12} textColor='purple' fontWeight='bold'>
          MEMBER ID
        </Text>
      </Column>
      <Column
        width={280}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
        justifyContent='center'
      >
        <Text fontSize={12} textColor='purple' fontWeight='bold'>
          NAME
        </Text>
      </Column>
      <Column
        width={175}
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
        justifyContent='center'
      >
        <ActivityFilterSelect
          selectedActivities={selectedActivities}
          onFilter={onActivityFilter}
        />
      </Column>
      <Column
        width={175 + scrollbarWidth}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
        justifyContent='center'
      >
        <Button
          shape='rectangular'
          size='small'
          variant='gray'
          justifyContent='space-between'
          backgroundColor='transparent'
          p='00'
          onPress={onSortToggle}
        >
          <Text fontSize={12} textColor='purple' fontWeight='bold'>
            DAYS SINCE LAST ENGAGEMENT
          </Text>
          <ToggleArrowIcon
            fill='purple'
            state={sortOrder === 'DESC' ? 'down' : 'up'}
            ml='12'
          />
        </Button>
      </Column>
    </Row>
  )
}

/* -----------------------------------------------------------------------------
 * MemberRow
 * -------------------------------------------------------------------------- */

function MemberRow({ member }: { member: CompanyMemberItem }) {
  const days = differenceInDays(
    new Date(),
    new Date(member.last_engagement_date)
  )

  return (
    <Row
      borderColor='shade02'
      borderStyle='solid'
      backgroundColor='white'
      borderBottomWidth={1}
    >
      <Column width={110} paddingX='16' paddingY='04'>
        <BoxLink path={`/chat/${member.user_id}`}>
          <Text fontSize={13} textColor='violet' numberOfLines={1}>
            {member.user_id}
          </Text>
        </BoxLink>
      </Column>
      <Column
        width={280}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
      >
        <BoxLink path={`/chat/${member.user_id}`}>
          <Text fontSize={13} textColor='violet' numberOfLines={1}>
            {member.first_name} {member.last_name}
          </Text>
        </BoxLink>
      </Column>
      <Column
        width={175}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
      >
        <Text fontSize={13} textColor='purple'>
          {member.last_engagement
            ? formatActivity(member.last_engagement)
            : 'N/A'}
        </Text>
      </Column>
      <Column
        width={175}
        paddingX='16'
        paddingY='04'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
      >
        <Text fontSize={13} textColor='purple'>
          {`${days == 0 ? 'To' : `${days} `}day${days > 0 ? 's' : ''}${
            days > 0 ? ' ago' : ''
          }`}
        </Text>
      </Column>
    </Row>
  )
}

/* -----------------------------------------------------------------------------
 * CompanyMembers
 * -------------------------------------------------------------------------- */

function CompanyMembers() {
  const navigate = useNavigate()
  const { companyId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { fetchCompanyMembers, fetchCompaniesList } = useMembersStore.getState()
  const [isLoading, setIsLoading] = useState(true)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)
  const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
    const v = searchParams.get('order_by')
    return v === 'ASC' || v === 'DESC' ? v : 'DESC'
  })
  const [selectedActivities, setSelectedActivities] = useState<string[]>(() => {
    const v = searchParams.get('last_engagement')
    return v ? v.split(',').filter(Boolean) : []
  })
  const [members, setMembers] = useState<CompanyMemberItem[]>([])
  const [page, setPage] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [companyName, setCompanyName] = useState<string>('')
  const [companyQty, setCompanyQty] = useState<number | null>(null)
  const [totalEvents, setTotalEvents] = useState<number | null>(null)
  const [scrollbarWidth, setScrollbarWidth] = useState(0)

  useLayoutEffect(() => {
    setScrollbarWidth(getScrollbarWidth())
  }, [])

  useEffect(() => {
    if (!companyId) return
    fetchCompaniesList().then(list => {
      const found = list.find(c => String(c.company_id) === String(companyId))
      if (found) {
        setCompanyName(found.company_name)
        setCompanyQty(found.qty)
      }
    })
  }, [companyId])

  useEffect(() => {
    if (!companyId) {
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    setPage(0)
    fetchCompanyMembers(companyId, {
      order_by: SORT_ORDER_API[sortOrder],
      last_engagement:
        selectedActivities.length > 0 ? selectedActivities : undefined,
      page: 0,
      page_size: PAGE_SIZE
    })
      .then(data => {
        setMembers(data?.results ?? [])
        setTotalEvents(data?.total_events ?? null)
        setHasNextPage(data?.has_next_page ?? false)
      })
      .finally(() => setIsLoading(false))
  }, [companyId, sortOrder, selectedActivities])

  useEffect(() => {
    const params = new URLSearchParams()
    params.set('order_by', sortOrder)
    if (selectedActivities.length > 0) {
      params.set('last_engagement', selectedActivities.join(','))
    }
    setSearchParams(params, { replace: true })
  }, [sortOrder, selectedActivities])

  const fetchNextPage = () => {
    if (!hasNextPage || isFetchingNextPage || !companyId) return
    const nextPage = page + 1
    setIsFetchingNextPage(true)
    fetchCompanyMembers(companyId, {
      order_by: SORT_ORDER_API[sortOrder],
      last_engagement:
        selectedActivities.length > 0 ? selectedActivities : undefined,
      page: nextPage,
      page_size: PAGE_SIZE
    })
      .then(data => {
        setMembers(prev => [...prev, ...(data?.results ?? [])])
        setTotalEvents(data?.total_events ?? null)
        setHasNextPage(data?.has_next_page ?? false)
        setPage(nextPage)
      })
      .finally(() => setIsFetchingNextPage(false))
  }

  const handleSortToggle = () => {
    setSortOrder(prev => (prev === 'DESC' ? 'ASC' : 'DESC'))
  }

  return (
    <AppShell activePage='members' label='Members By Company'>
      <Box alignItems='center' flexDirection='row' mb='08'>
        <HeaderBackButton
          accessibilityHint='Go back to members'
          accessibilityLabel='Back'
          accessibilityRole='button'
          ml='-12'
          onPress={() => navigate('/members')}
        />
      </Box>

      <PageHeader
        eyebrow='Members'
        mb='08'
        title={`${companyName || 'Company'} (${
          isLoading ? '…' : companyQty ?? members.length
        })`}
      />

      {!isLoading && selectedActivities.length > 0 && totalEvents !== null && (
        <Text font='pro.text.12.regular' mb='24' textColor='proTextMuted'>
          Filtering {totalEvents} members from {companyQty ?? '?'} available for{' '}
          {companyName}
        </Text>
      )}
      {!isLoading &&
        selectedActivities.length === 0 &&
        totalEvents !== null && (
          <Text font='pro.text.12.regular' mb='24' textColor='proTextMuted'>
            Displaying {totalEvents} members available for {companyName}
          </Text>
        )}

      {isLoading ? (
        <Box
          alignItems='center'
          flex='1 1 auto'
          justifyContent='center'
          py='40'
        >
          <Loading />
        </Box>
      ) : (
        <>
          <MemberHeader
            sortOrder={sortOrder}
            onSortToggle={handleSortToggle}
            selectedActivities={selectedActivities}
            onActivityFilter={setSelectedActivities}
            scrollbarWidth={scrollbarWidth}
          />
          <FlatList
            ListEmptyComponent={() => (
              <Row
                borderColor='shade02'
                borderStyle='solid'
                backgroundColor='white'
                borderBottomWidth={1}
              >
                <Column flex='1 1 auto' paddingX='16' paddingY='08'>
                  <Text fontSize={13} textColor='purple'>
                    No members found, adjust your filters or try another
                    company.
                  </Text>
                </Column>
              </Row>
            )}
            data={members}
            keyExtractor={(item: unknown) =>
              String((item as CompanyMemberItem).engagement_id)
            }
            initialNumToRender={PAGE_SIZE}
            onEndReached={fetchNextPage}
            onEndReachedThreshold={0.5}
            renderItem={({ item }) => (
              <MemberRow member={item as CompanyMemberItem} />
            )}
            ListFooterComponent={
              isFetchingNextPage ? (
                <Box alignItems='center' justifyContent='center' py='16'>
                  <Loading />
                </Box>
              ) : null
            }
          />
        </>
      )}
    </AppShell>
  )
}

export default CompanyMembers
